/**
 * Account notification settings from Cispracs ID (`POST /oauth/notify-prefs`)
 * and the one rule that applies them — shared so the server's push fan-out and
 * the web client read them the same way.
 *
 * The rule is a ceiling: the account setting only allows, it never turns
 * anything on. A card pops up only if the account allows `popup` AND Bunker's
 * own switches let it through; the same for `sound`; a push goes out only if
 * the account allows `push` AND the device holds a push subscription. Bunker's
 * device switches are never written from the account.
 *
 * Topics: `dm` is a direct or group message, `mention` a mention of me or a
 * reply to me on a server. Anything else (a plain server message) has no topic
 * and the account does not touch it.
 *
 * `null` prefs mean "no account restriction": the switch is off, or the
 * platform has not answered yet and there is no earlier answer.
 */

export type AccountNotifyTopic = 'dm' | 'mention';
export type AccountNotifyChannel = 'popup' | 'sound' | 'push';

export interface AccountNotifyTopicPrefs {
  popup: boolean;
  sound: boolean;
  push: boolean;
}

export interface AccountNotifyPrefs {
  dm: AccountNotifyTopicPrefs;
  mention: AccountNotifyTopicPrefs;
}

/** What the server tells a client, in `ready.notifyPrefs` and the `me:notify-prefs`
 *  event. `enabled: false` — the switch is off, `prefs` is then always null. */
export interface AccountNotifyPrefsState {
  enabled: boolean;
  prefs: AccountNotifyPrefs | null;
}

/** The platform's defaults (and Bunker's answer to `404 unknown_lineage`):
 *  everything on except sound. */
export const ACCOUNT_NOTIFY_DEFAULTS: AccountNotifyPrefs = Object.freeze({
  dm: Object.freeze({ popup: true, sound: false, push: true }),
  mention: Object.freeze({ popup: true, sound: false, push: true }),
}) as AccountNotifyPrefs;

function readTopic(raw: unknown): AccountNotifyTopicPrefs | null {
  if (!raw || typeof raw !== 'object') return null;
  const t = raw as Record<string, unknown>;
  if (typeof t.popup !== 'boolean' || typeof t.sound !== 'boolean' || typeof t.push !== 'boolean') return null;
  return { popup: t.popup, sound: t.sound, push: t.push };
}

/**
 * Read the `prefs` object of a platform notify-prefs answer (or of our own
 * `ready.notifyPrefs` / `me:notify-prefs`). Extra topics and fields are ignored; a missing or
 * non-boolean known field makes the whole answer unusable (null) — the caller
 * then keeps its last good answer instead of guessing.
 */
export function parseAccountNotifyPrefs(raw: unknown): AccountNotifyPrefs | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const dm = readTopic(p.dm);
  const mention = readTopic(p.mention);
  if (!dm || !mention) return null;
  return { dm, mention };
}

/** Which account topic an incoming message falls under, or null for none. */
export function accountNotifyTopic(s: {
  isDm: boolean;
  mentioned: boolean;
  repliedTo: boolean;
}): AccountNotifyTopic | null {
  if (s.isDm) return 'dm';
  if (s.mentioned || s.repliedTo) return 'mention';
  return null;
}

/**
 * Does the account allow this channel (`popup`/`sound`/`push`) for this topic?
 * True with no prefs (no restriction) and for a message with no topic. This is
 * only the account half of the ceiling: callers AND it with their own switches.
 */
export function accountAllows(
  prefs: AccountNotifyPrefs | null | undefined,
  topic: AccountNotifyTopic | null,
  channel: AccountNotifyChannel,
): boolean {
  if (!prefs || !topic) return true;
  return prefs[topic][channel];
}
